import { IPendingPhotoVerificationRepository } from '../../application/ports/outbound/IPendingPhotoVerificationRepository';
import { PendingPhotoVerificationResult } from '../../application/domain/entities/PendingPhotoVerificationResult';
import { SortOrder } from "@/shared/application/domain/enums/SortOrder";
import { HttpClient } from '@/shared/infra/http/HttpClient';
import { PendingPhotoVerificationResponseDto, PendingPhotoVerificationResponseItemDto } from './dto/res/PendingPhotoVerificationResponseDto';
import { PendingPhotoVerificationMapper } from './mappers/PendingPhotoVerificationMapper';
import { PhotoMatchDecisionRequestDto } from './dto/req/PhotoMatchDecisionRequestDto';
import { PhotoMatchDecisionResponseDto } from './dto/res/PhotoMatchDecisionResponseDto';
import {
  PhotoMatchDecision,
  PhotoMatchDecisionResponse,
  PhotoMatchDetailResponse,
} from '../../application/ports/outbound/IPendingPhotoVerificationRepository';
import { PhotoMatchDetailResponseDto } from './dto/res/PhotoMatchDetailResponseDto';
import { PhotoMatchMapper } from './mappers/PhotoMatchMapper';

// Type guard to check if response is paginated
function isPaginatedResponse(
  data: unknown
): data is PendingPhotoVerificationResponseDto {
  return (
    typeof data === 'object' &&
    data !== null &&
    'data' in data &&
    'pagination' in data &&
    Array.isArray((data as PendingPhotoVerificationResponseDto).data)
  );
}

// Type guard to check if response is an array
function isArrayResponse(
  data: unknown
): data is PendingPhotoVerificationResponseItemDto[] {
  return Array.isArray(data);
}

export class ApiPendingPhotoVerificationRepository implements IPendingPhotoVerificationRepository {
  constructor(private readonly httpClient: HttpClient) {}

  async fetchPendingPhotoVerificationRecords(
    page: number,
    pageSize: number,
    columnName?: string,
    sortOrder?: SortOrder
  ): Promise<PendingPhotoVerificationResult> {
    try {
      const queryParams = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString(),
        caseStatus: 'PHOTO_MATCH',
        isUserActionRequired: 'true',
      });
      if (columnName) {
        queryParams.append('columnName', columnName);
      }
      if (sortOrder) {
        queryParams.append('sortOrder', sortOrder);
      }

      const url = `/e-verify/case-summary?${queryParams.toString()}`;
      const response = await this.httpClient.get<
        PendingPhotoVerificationResponseDto | PendingPhotoVerificationResponseItemDto[]
      >(url);

      // Extract the actual data from the ApiResponse wrapper
      const responseData = response?.data ?? response;

      // Use type guards to determine the response shape
      if (isPaginatedResponse(responseData)) {
        return PendingPhotoVerificationMapper.toDomainList(responseData);
      } else if (isArrayResponse(responseData)) {
        // Handle legacy array response format
        return PendingPhotoVerificationMapper.toDomainList({
          data: responseData,
          pagination: {
            currentPage: page,
            itemsPerPage: pageSize,
            totalItems: responseData.length,
            totalPages: 1,
          },
        });
      } else {
        // Fallback for unexpected response format
        return PendingPhotoVerificationMapper.toDomainList({
          data: [],
          pagination: {
            currentPage: page,
            itemsPerPage: pageSize,
            totalItems: 0,
            totalPages: 0,
          },
        });
      }
    } catch (error) {
      console.error('Error fetching pending photo verification records:', error);
      throw error;
    }
  }

  async confirmPhotoMatch(_i9FormId: string, eVerifyCase: string): Promise<void> {
    await this.submitPhotoMatchDecision(eVerifyCase, 'matching');
  }

  async reportPhotoMismatch(
    _i9FormId: string,
    eVerifyCase: string,
    _reason?: string
  ): Promise<void> {
    await this.submitPhotoMatchDecision(eVerifyCase, 'not-matching');
  }

  async getPhotoMatchDetails(_caseId: string): Promise<PhotoMatchDetailResponse> {
    const response = await this.httpClient.get<PhotoMatchDetailResponseDto>(
      `/e-verify/photo-match/${_caseId}`
    );
    return PhotoMatchMapper.toDetailResponse(response.data);
  }

  async submitPhotoMatchDecision(
    caseId: string,
    decision: PhotoMatchDecision
  ): Promise<PhotoMatchDecisionResponse> {
    const payload: PhotoMatchDecisionRequestDto = { photoMatch: decision };
    const response = await this.httpClient.post<PhotoMatchDecisionResponseDto>(
      `/e-verify/photo-match/${caseId}`,
      payload
    );
    return PhotoMatchMapper.toDecisionResponse(response.data);
  }
}
